<template>
  <section class="main">
    <loader v-if="!configLoaded" />
    <h1 v-if="configLoaded && isDeviceLinked" class="intro">
      <html-with-links tag="span" class="intro__inner" :html="$t('INSTALL_APPS_WITH_DEV_ACCOUNT')" />
    </h1>
    <div v-if="configLoaded" class="row">
      <div class="col col_1">
        <template v-if="isDeviceLinked">
          <important-message v-if="hasPremium" type="success">
            {{ $t('PREMIUM_ALREADY_ACTIVE') }}
          </important-message>

          <table class="table">
            <template v-if="!config.plus_account?.appleid
              || config.plus_account?.is_yours === 0
              || configuring
              || showConfigure">
              <tr>
                <td class="cell">
                  Apple account {{ $t('OF_YOUR_OWN_DEV_ACCOUNT') }}<br />
                  <a href="https://www.youtube.com/watch?v=-ShhaoaLWyE" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
                </td>
                <td class="td">
                  <input class="input" v-model="appleid" />
                </td>
              </tr>
              <tr>
                <td class="cell">
                  {{ $t('PASSWORD') }}
                </td>
                <td class="td">
                  <input class="input" type="password" v-model="password" />
                </td>
              </tr>
              <tr>
                <td class="cell">
                </td>
                <td class="td">
                  <html-with-links tag="span" class="hint"
                    :html="$t('PLUS_PRIVACY_HINT', { privacy_href: '/info/privacy' })" />
                </td>
              </tr>
              <tr>
                <td class="cell">
                  {{ $t('SEND_SMS_TO_VERIFY_ACCOUNT') }}
                </td>
                <td class="td">
                  <yes-no v-model="isSMSAuth" :options="[{ label: $t('YES'), value: 1 }, { label: $t('NO'), value: 0 }]"
                    fit-content />
                  <html-with-links tag="span" class="hint hint_mt" :html="$t('SMS_AUTH_HINT')" />
                </td>
              </tr>
              <tr>
                <td class="cell"></td>
                <td class="td">
                  <btn class="configure-button" type="primary" :loading="configuring" :disabled="!appleid || !password"
                    @click.native="configurePlusAccount">
                    {{ $t('CONFIGURE_PLUS_ACCOUNT') }}
                  </btn>
                </td>
              </tr>
            </template>
            <tr v-if="config.plus_account?.appleid && config.plus_account?.is_yours !== 0">
              <td class="cell">
                Apple account<br />
                <a href="https://www.youtube.com/watch?v=-ShhaoaLWyE" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
              </td>
              <td class="td">
                {{ config.plus_account?.appleid }}
                <template v-if="!showConfigure">
                  <br />
                  <btn class="configure-button" type="primary" @click.native="reconfigureAccount">
                    {{ $t('RECONFIGURE_YOUR_ACCOUNT') }}
                  </btn>
                </template>
              </td>
            </tr>
            <tr v-if="!config.plus_account?.shared_by && config.plus_account?.appleid">
              <td class="cell"></td>
              <td class="td">
                <btn class="delete-account-button" type="negative" :loading="deletingAccount"
                  @click.native="deleteAccount">
                  {{ $t('DELETE_PLUS_DEV_ACCOUNT') }}
                </btn>
              </td>
            </tr>
            <tr
              v-if="config.plus_account?.shared_by && config.app_installation_method !== 'premium_shared_dev_account'">
              <td class="cell">
                {{ $t('SHARED_WITH_YOU_BY') }}
              </td>
              <td class=td>{{ config.plus_account?.shared_by }}<br /><router-link
                  v-if="config.plus_account?.is_yours === 0 && config.p2p_purchase_id"
                  :to="{ name: 'p2p-discussion', query: { id: config.p2p_purchase_id } }">
                  {{ $t('CHAT_WITH_OWNER') }}
                </router-link></td>
            </tr>
            <tr v-if="config.plus_account?.status_translated">
              <td class="cell">
                {{ $t('STATUS') }}
              </td>
              <td class="td">
                <span v-html="config.plus_account?.status_translated"></span>
                <template v-if="config.plus_account?.status === 'otp_required' && !configuring">
                  <br />
                  <btn class="configure-button" type="primary" :loading="sendingOtp" @click.native="sendOtp">
                    {{ $t('ENTER_OTP_SHORT') }}
                  </btn>
                </template>
                <template v-if="config.plus_account?.status === 'ok'
                  && !config.plus_account?.shared_by">
                  <br />
                  <btn class="configure-button" type="primary" @click.native="downloadArchive">
                    {{ $t('DOWNLOAD_ARCHIVE_FOR_DEVELOPMENT') }}
                  </btn>
                </template>
              </td>
            </tr>
            <tr v-if="config.plus_account?.appleid && !config.plus_account?.shared_by">
              <td class="cell">
                {{ $t('SHARED_WITH_FRIENDS') }}<br />
                <a href="https://www.youtube.com/watch?v=v0u7c4z59-s" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
              </td>
              <td class="td">
                <div class="shared-row" v-for="account in config.plus_account?.shared_with" :key="account.email">
                  {{ account.email }}
                  <span style="white-space: nowrap">
                    ({{ $t('DEVICES_COUNT', { n: account.devices_amount }) }})
                  </span>&nbsp;&nbsp;<btn type="negative" class="unsh-button"
                    :loading="unsharing.includes(account.email)" @click.native="unshare(account.email)">{{ $t('Remove')
                    }}</btn>
                </div>
                <btn type="primary" class="sh-action" @click.native="share" :loading="sharing">{{
                  $t('SHARE_WITH_FRIEND') }}
                </btn>
              </td>
            </tr>
            <tr v-if="config.plus_account && config.plus_account?.is_yours !== 0">
              <td class="cell">
                {{ $t('REIMBURSEMENT') }}
                <br />
                <router-link to="/my/auto-reimbursement">{{ $t('LEARN_MORE') }}</router-link>

              </td>
              <td class="td">
                <template v-if="config.plus_account?.is_automatic_reimbursement">
                  {{ $t('ENABLED') }}
                </template>
                <template v-if="config.plus_account?.status === 'revoked'">
                  <br />
                  <btn class="configure-button" type="primary" :loading="archivingAccount"
                    @click.native="archiveRevokedAccount">
                    Archive revoked account
                  </btn>
                </template>
                <template v-else>
                  {{ $t('DISABLED') }}
                </template>
              </td>
            </tr>
            <tr v-if="!config.plus_account || config.plus_account?.is_yours === 0">
              <td class="cell">
                {{ $t('REIMBURSEMENT') }}
              </td>
              <td class="td">
                <router-link to="/my/auto-reimbursement">{{ $t('LEARN_MORE') }}</router-link>
              </td>
            </tr>
            <tr v-if="config.plus_account?.is_yours === 1 && config.plus_account?.is_automatic_reimbursement === 0">
              <td colspan="2">
                <!--<router-link to="/my/auto-reimbursement" class="banner">
                  <span class="banner__title">
                    {{ $t('GET_N_CASHBACK', { amount: '100%' }) }}
                  </span>
                  <span class="banner__desc">
                    {{ $t('LEARN_MORE') }}
                  </span>
                </router-link>-->
              </td>
            </tr>
          </table>
        </template>
      </div>
      <aside class="sidebar col col_2">
        <p class="sidebar__text g-text" v-html="$t('DEV_ACCOUNT_PAGE_SIDEBAR')"></p>
      </aside>
    </div>
  </section>
</template>

<script>
import Loader from '@/components/Loader';
import Btn from '@/components/Button.vue';
import YesNo from '@/components/YesNo.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import ImportantMessage from '@/components/ImportantMessage.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    Loader,
    Btn,
    HtmlWithLinks,
    YesNo,
    ImportantMessage,
  },
  data() {
    return {
      loading: false,
      configuring: false,
      sendingOtp: false,
      showConfigure: false,
      deletingAccount: false,
      archivingAccount: false,
      sharing: false,
      unsharing: [],
      appleid: '',
      password: '',
      isSMSAuth: 0,
      plusOptions: [],
    };
  },
  computed: {
    ...mapGetters(['hasPlus', 'hasActivePlus', 'hasCanceledPlus', 'devicesLoaded', 'isDeviceLinked', 'hasPremium']),
    title() {
      return this.$t('INSTALL_CONFIGURE_YOUR_DEV_ACCOUNT_PLUS');
    },
    configLoaded() {
      return !!this.config.model || (this.devicesLoaded && !this.isDeviceLinked);
    },
    config() {
      return this.$store.getters.config;
    },
    status() {
      if (this.hasPlus) {
        return 'ACTIVATED';
      }

      return 'NOT_ACTIVATED';
    },
  },
  methods: {
    cancelSubscription() {
      this.$confirm(this.$t('PLUS_SUBSCRIPTION_CANCEL_CONFIRMATION'))
        .then(() => this.$api.cancelPlusSubscription())
        .then((response) => {
          if (response) {
            window.location.href = response;
            return;
          }

          this.$notify({
            type: 'error',
            text: this.$t('SOMETHING_WENT_WRONG'),
          });
        });
    },
    async configurePlusAccount() {
      if (!this.config.plus_account?.appleid) {
        try {
          await this.$confirm(this.$t('PLUS_DEV_ACCOUNT_FIRST_CONFIGURE_CONFIRMATION'), { yesOrNo: true });
        } catch {
          this.$router.push('/my/faq?accent=step-1#official');
          return;
        }
      }

      this.configuring = true;
      const result = await this.$api.editPlusDevAccount({
        appleid: this.appleid,
        password: this.password,
        isSMSAuth: this.isSMSAuth,
        status: this.config.plus_account?.appleid ? 'new' : '',
        // eslint-disable-next-line camelcase
        sharedWithEmails: this.config.plus_account?.shared_with?.map(a => a.email) || [],
      })
        .catch(e => e);

      if (result instanceof Error) {
        this.configuring = false;
        return;
      }

      this.handleStatus(this.appleid)
        .finally(() => {
          this.configuring = false;
        });
    },
    async handleStatus(appleid) {
      await this.$store.dispatch('getConfig');
      const { status } = this.config.plus_account;

      if (status === 'ok') {
        this.showConfigure = false;
        this.$notify({
          type: 'success',
          text: this.$t('PLUS_ACCOUNT_CONFIGURED'),
        });
        return true;
      }

      if (status === 'error') {
        this.$notify({
          type: 'error',
          text: this.config.plus_account.status_translated,
        });
        return false;
      }

      if (status === 'revoked') {
        // Stop polling if account is revoked
        return false;
      }

      if (status === 'otp_required') {
        const otp = await this.$prompt(this.$t('ENTER_OTP'));

        // Validate OTP - ensure it's exactly 6 digits
        const trimmedOtp = otp.trim();
        if (!/^\d{6}$/.test(trimmedOtp)) {
          this.$notify({
            type: 'error',
            text: this.$t('OTP_MUST_BE_6_DIGITS'),
          });
          // Try again
          return this.handleStatus(appleid);
        }

        await this.$api.editPlusDevAccount({
          appleid,
          otp: trimmedOtp,
          sharedWithEmails: this.config.plus_account.shared_with.map(a => a.email),
        });
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.handleStatus(appleid));
        }, 1000);
      });
    },
    async unshare(email) {
      this.$confirm(this.$t('DEV_ACCOUNT_SHARE_REMOVAL_CONFIRMATION', { email }))
        .then(() => {
          this.unsharing.push(email);
          return this.$api.editPlusDevAccount({
            appleid: this.config.plus_account.appleid,
            status: 'new',
            sharedWithEmails:
              this.config.plus_account.shared_with.map(a => a.email)
                .filter(e => e !== email),
          });
        })
        .then(() => this.handleStatus(this.config.plus_account.appleid))
        .finally(() => {
          const idx = this.unsharing.indexOf(email);
          this.unsharing.splice(idx, 1);
        });
    },
    share() {
      this.$prompt(this.$t('DEV_ACCOUNT_SHARE_PROMPT'))
        .then((email) => {
          this.sharing = true;
          return this.$api.editPlusDevAccount({
            appleid: this.config.plus_account.appleid,
            status: 'new',
            sharedWithEmails: [...this.config.plus_account.shared_with.map(a => a.email), email],
          });
        })
        .then(() => this.handleStatus(this.config.plus_account.appleid))
        .finally(() => {
          this.sharing = false;
        });
    },
    deleteAccount() {
      this.$confirm(this.$t('DELETE_PLUS_DEV_ACCOUNT_CONFIRMATION'))
        .then(() => {
          this.deletingAccount = true;
          return this.$api.deletePlusDevAccount();
        })
        .then(() => this.$store.dispatch('getConfig'))
        .finally(() => {
          this.deletingAccount = false;
        });
    },
    archiveRevokedAccount() {
      this.$confirm('This action will archive your account, so you can configure another one. After archiving, current developer account can not be used with appdb.')
        .then(() => {
          this.archivingAccount = true;
          return this.$api.archiveRevokedPlusDevAccount();
        })
        .then(() => {
          this.$notify({
            type: 'success',
            text: 'Account was archived successfully. You can check it\'s historical data at <a href="/my/auto-reimbursement">Reimbursement program page</a>',
          });
          // Reload page data since account is deleted on backend after archiving
          return Promise.all([
            this.$store.dispatch('getConfig'),
            this.$store.dispatch('getDevices'),
          ]);
        })
        .catch((error) => {
          // Only log error if it's not a cancellation
          if (error) {
            console.error('Error archiving revoked account:', error);
          }
        })
        .finally(() => {
          this.archivingAccount = false;
        });
    },
    sendOtp() {
      this.sendingOtp = true;
      this.handleStatus(this.config.plus_account.appleid)
        .finally(() => {
          this.sendingOtp = false;
        });
    },
    downloadArchive() {
      this.$api.getPlusDevAccountArchive()
        .then((response) => {
          if (response) {
            window.location.href = response;
            return;
          }

          this.$notify({
            type: 'error',
            text: this.$t('SOMETHING_WENT_WRONG'),
          });
        });
    },
    reconfigureAccount() {
      if (this.config.plus_account?.appleid && this.config.plus_account.appleid.includes('@')) {
        this.appleid = this.config.plus_account.appleid;
      }
      this.showConfigure = true;
    },
  },
  mounted() {
    const el = this.$route.hash && document.querySelector(this.$route.hash);
    if (el) {
      el.scrollIntoView();
    }
  },
  created() {
    this.$api.getSideloadingOptions('plus').then((response) => {
      this.plusOptions = response;
    });

    // Check if account is in verifying status and start polling
    this.$store.dispatch('getConfig').then(() => {
      if (this.config.plus_account) {
        if (this.config.plus_account.status === 'otp_required') {
          // Automatically trigger OTP entry if status is OTP required
          this.sendOtp();
        } else if (this.config.plus_account.status !== 'ok'
          && this.config.plus_account.status !== 'error'
          && this.config.plus_account.status !== 'revoked') {
          // Start checking status if it's in a verifying state
          this.handleStatus(this.config.plus_account.appleid);
        }
      }
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.error-wrapper {
  display: flex;
  margin-bottom: 15px;
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-right: 5px;
  flex-grow: 1;
  font-size: 14px;

  &_mb {
    margin-bottom: 20px;

    @include tablet {
      margin-bottom: 15px;
    }
  }
}

.main {
  display: block;
  flex-grow: 1;
}

.intro {
  &__inner {
    margin-right: 5px;
  }

  &__status {
    font-size: inherit;
    font-weight: 400;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.col {
  &_1 {
    flex-grow: 1;
  }

  &_2 {
    flex: 0 0 192px;
    margin-left: 15px;

    @include notebook {
      margin-left: 0;
      margin-top: 15px;
    }
  }

  @include notebook {
    flex: 0 0 100%;
  }
}

.table {
  line-height: 18px;
  margin-bottom: 15px;

  tr:not(:first-child) td {
    padding-top: 10px;
  }
}

.td {
  padding-left: 40px;
}

.cell {
  width: 140px;
  flex: 0 0 140px;
  word-break: break-word;
}

.provided {
  padding-top: 15px;
  border-top: 1px solid #e5e5e5;
  display: flex;

  &_alone {
    flex-direction: column;

    .provided__support {
      max-width: 100%;
    }

    .provided__link {
      margin-top: 0;
    }
  }

  &__val {
    padding-left: 40px;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__link {
    color: var(--text-primary);
    display: flex;
    align-items: center;
    margin: 7px 0;
  }

  &__icon {
    margin-right: 5px;
  }

  &__support {
    font-size: 12px;
    font-weight: 400;
    max-width: 300px;
  }
}

.button {
  margin-left: 5px;
}

.sidebar {
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;

  &__heading {
    font-weight: 500;
    margin-bottom: 10px;
  }

  &__text {
    &::v-deep {
      p:not(:last-child) {
        margin-bottom: 15px;
      }
    }
  }
}

.list {
  list-style: disc inside;
  margin-bottom: 15px;

  &__item {
    &:not(:last-child) {
      margin-bottom: 5px;
    }
  }
}

.buy {
  margin-bottom: 15px;
}

.buy:not(:first-child) {
  margin-top: 25px;
}

.cancel-button,
.configure-button {
  margin-left: 0;

  &:not(:first-child) {
    margin-top: 5px;
  }
}

.input {
  background: var(--tertiary-bg);
  border: 1px solid var(--tertiary-accent);
  border-radius: 2px;
  padding: 4px 6px;
  margin-left: auto;
  flex: 0 0 150px;
  width: 150px;
  font-size: 14px;
  font-family: "IBM Plex Sans", sans-serif;
  color: var(--text-primary);

  &::placeholder {
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);
  }
}

.hint {
  display: inline-block;
  font-size: 12px;
  font-weight: 400;
  max-width: 300px;
  line-height: 1.2;

  &_mt {
    margin-top: 10px;
  }
}

.warning {
  color: var(--configure-error-text);
  background: var(--bg-error);
  border-radius: 2px;
  padding: 3px 10px;
  text-align: center;
  line-height: 18px;
  margin-bottom: 20px;
}

.shared-row {
  margin-bottom: 5px;
  line-height: 1.6;

  &+.sh-action {
    margin-top: 5px;
  }
}

.unsh-button,
.sh-action,
.delete-account-button {
  margin-left: 0;
}

.banner {
  display: block;
  border-radius: 8px;
  padding: 15px;
  background-image: $my-buy-options-gradient;
  color: #fff;
  font-weight: 500;
  text-align: center;
  cursor: pointer;

  @include tablet {
    padding: 10px;
  }

  &__title {
    display: block;
    font-size: 16px;
    margin-bottom: 5px;

    @include tablet {
      font-size: 14px;
    }
  }

  &__desc {
    display: block;
    font-size: 20px;
    white-space: nowrap;

    @include tablet {
      font-size: 14px;
      font-weight: 700;
    }
  }
}
</style>
